import React, { useEffect, useMemo, useState } from 'react';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
} from 'components/mui';
import { PurchasesByEmail } from 'api/lotusBlossom/lotusBlossomAPI';
import ParticipantsGrid from './ParticipantsGrid';
import { findPastSessions, formatSessionDate, ThemeSession } from '../libs/themeLists';

interface PastSessionsPanelProps {
	sessions: ThemeSession[];
	subscribersListId: number | null;
	/** From the page's single Stripe read; null while it is still loading. */
	purchases: PurchasesByEmail | null;
}

/**
 * Rosters for sessions that have already happened. Membership is rebuilt as of
 * the session date rather than read from the subscribers list, which only ever
 * holds who is subscribed right now.
 */
function PastSessionsPanel({
	sessions,
	subscribersListId,
	purchases,
}: PastSessionsPanelProps) {
	const pastSessions = useMemo(() => findPastSessions(sessions), [sessions]);
	const [selectedId, setSelectedId] = useState<number | ''>('');

	// Default to the most recent session, and recover if a reload drops the one
	// that was selected.
	useEffect(() => {
		const stillPresent = pastSessions.some((s) => s.listId === selectedId);
		if (!stillPresent) {
			setSelectedId(pastSessions[0]?.listId ?? '');
		}
	}, [pastSessions, selectedId]);

	const selected = pastSessions.find((s) => s.listId === selectedId) ?? null;

	if (pastSessions.length === 0) {
		return (
			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					No past sessions yet
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					Sessions appear here once they&apos;ve finished.
				</Typography>
			</Paper>
		);
	}

	return (
		<>
			<Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, mb: 2 }}>
				<FormControl size="small" fullWidth sx={{ maxWidth: 520 }}>
					<InputLabel id="past-session-label">Session</InputLabel>
					<Select
						labelId="past-session-label"
						label="Session"
						value={selectedId}
						onChange={(e) => setSelectedId(Number(e.target.value))}
					>
						{pastSessions.map((session) => (
							<MenuItem key={session.listId} value={session.listId}>
								{formatSessionDate(session)} — {session.themeName}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Paper>

			{selected && (
				<ParticipantsGrid
					// Remount on change, so the previous roster isn't left on screen
					// while the new one loads.
					key={selected.listId}
					session={selected}
					subscribersListId={subscribersListId}
					asOfDate={selected.dateISO}
					purchases={purchases}
				/>
			)}
		</>
	);
}

export default PastSessionsPanel;
